/**
 * DeductionsTab — Administración cross-empleado de descuentos recurrentes
 * mensuales (Seguro Complementario, FALP, créditos, cuotas, etc.)
 * y créditos / préstamos al trabajador (CCAF, internos, judiciales).
 */
import { todayISO } from '../../../lib/utils'
import { useMemo, useState } from 'react'
import { EntityAutocomplete } from '@/components/EntityAutocomplete'
import { Plus, Pencil, Trash2, Users, CalendarClock, AlertTriangle } from 'lucide-react'
import { NumericInput } from '@/components/NumericInput'
import { FilterBar } from '@/components/filters/FilterBar'
import { useFilters } from '@/components/filters/useFilters'
import type { FilterConfig } from '@/components/filters/types'
import { BulkAssignModal } from './BulkAssignModal'
import apiClient from '@/lib/apiClient'
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query'
import { useTenantLocale, formatNumber } from '@/lib/locale'
import { usePermission } from '@/hooks/usePermission'
import { ToastContainer, useToast } from '@/components/Toast'
import { Modal } from '@/components/ds/Modal'
import { DataTable } from '@/components/ds/DataTable'
import DatePicker from '@/components/DatePicker'
import { inputCls, labelCls } from '@/lib/formStyles'
import { useEmployees } from '../hooks/usePayroll'
import { usePayrollDeductionTypes } from '@/modules/accounting/hooks/useAccounting'
import { TextField } from '@/components/ds/TextField'
import { SelectField } from '@/components/ds/SelectField'

interface RecurringDeduction {
  id: string
  employee: string
  employee_name?: string
  name: string
  amount: string
  deduction_type?: string | null
  deduction_type_name?: string | null
  effective_from: string | null
  effective_to: string | null
  is_active: boolean
  notes?: string
}

interface EmployeeLoan {
  id: string
  employee: string
  employee_name?: string
  loan_type: string
  loan_type_display?: string
  name: string
  total_amount: string
  total_installments: number
  installment_amount: string
  paid_installments: number
  remaining_installments: number
  start_date: string
  status: string
  status_display?: string
  folio?: string
}

function useDeductions() {
  return useQuery({
    queryKey: ['employee-recurring-deductions', 'all'],
    queryFn: async () => {
      const r = await apiClient.get('/payroll/employee-recurring-deductions/')
      return r.data as { deductions: RecurringDeduction[]; count: number }
    },
  })
}

function useLoans() {
  return useQuery({
    queryKey: ['employee-loans', 'all'],
    queryFn: async () => {
      const r = await apiClient.get('/payroll/employee-loans/')
      return r.data as { loans: EmployeeLoan[]; count: number }
    },
  })
}

function useSaveDeduction() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, data }: { id?: string; data: any }) => {
      const r = id
        ? await apiClient.patch(`/payroll/employee-recurring-deductions/${id}/`, data)
        : await apiClient.post('/payroll/employee-recurring-deductions/', data)
      return r.data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['employee-recurring-deductions'] }),
  })
}

function useDeleteDeduction() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/payroll/employee-recurring-deductions/${id}/`)
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['employee-recurring-deductions'] }),
  })
}

/**
 * Versiona en el tiempo el monto de varios descuentos: cierra los vigentes el día
 * antes del corte y crea la versión nueva desde esa fecha, con el monto propio de
 * cada trabajador. Es lo que la asignación masiva no puede hacer (escribe un solo
 * monto para todos y pisa el registro anterior en vez de versionarlo).
 */
function useRepriceDeductions() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: { effective_from: string; items: { id: string; amount: string }[] }) => {
      const r = await apiClient.post('/payroll/employee-recurring-deductions/reprice/', data)
      return r.data as { closed: number; created: number; errors: string[]; period_warning: string }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['employee-recurring-deductions'] }),
  })
}

function useSaveLoan() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, data }: { id?: string; data: any }) => {
      const r = id
        ? await apiClient.patch(`/payroll/employee-loans/${id}/`, data)
        : await apiClient.post('/payroll/employee-loans/', data)
      return r.data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['employee-loans'] }),
  })
}

function useDeleteLoan() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/payroll/employee-loans/${id}/`)
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['employee-loans'] }),
  })
}

function DeductionModal({ deduction, onClose }: { deduction?: RecurringDeduction; onClose: () => void }) {
  const isEdit = !!deduction
  const { data: emps } = useEmployees({ active: 'true', page_size: 9999 })
  const { data: types } = usePayrollDeductionTypes()
  const save = useSaveDeduction()
  const toast = useToast()
  const locale = useTenantLocale()
  const [employee, setEmployee] = useState(deduction?.employee || '')
  const [deductionType, setDeductionType] = useState(deduction?.deduction_type || '')
  const [name, setName] = useState(deduction?.name || '')
  const [amount, setAmount] = useState(deduction?.amount || '')
  const [from, setFrom] = useState(deduction?.effective_from || '')
  const [to, setTo] = useState(deduction?.effective_to || '')
  const [isActive, setIsActive] = useState(deduction?.is_active ?? true)

  const handleSubmit = async () => {
    if (!employee || !name.trim() || !amount) {
      toast.error('Completa empleado, nombre y monto.')
      return
    }
    try {
      await save.mutateAsync({
        id: deduction?.id,
        data: {
          employee, name: name.trim(), amount,
          deduction_type: deductionType || null,
          effective_from: from || null, effective_to: to || null,
          is_active: isActive,
        },
      })
      toast.success(isEdit ? 'Descuento actualizado' : 'Descuento creado')
      onClose()
    } catch {
      toast.error(isEdit ? 'Error al actualizar descuento' : 'Error al crear descuento')
    }
  }

  return (
    <Modal open title={isEdit ? 'Editar Descuento Recurrente' : 'Nuevo Descuento Recurrente'} onClose={onClose} size="sm"
      actions={
        <>
          <button onClick={onClose} className="px-4 py-2 text-sm rounded-lg border border-divider hover:bg-soft">Cancelar</button>
          <button onClick={handleSubmit} disabled={save.isPending} className="px-4 py-2 text-sm rounded-lg bg-primary text-on-primary hover:bg-primary-hover disabled:opacity-60">
            {save.isPending ? 'Guardando…' : 'Guardar'}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <div>
          <label htmlFor="deductionstab-empleado" className={labelCls}>Empleado *</label>
          {isEdit ? (
            <input aria-label="Empleado" value={deduction?.employee_name || ''} disabled className={inputCls} />
          ) : (
            <EntityAutocomplete id="deductionstab-empleado"
              options={(emps?.employees || []).map((e: any) => ({ id: e.id, label: `${e.full_name} (${e.rut})` }))}
              value={employee}
              onChange={setEmployee}
              placeholder="— Seleccionar —"
              required
            />
          )}
        </div>
        <SelectField
          dense
          label="Tipo de descuento"
          value={deductionType || ''}
          onChange={e => {
              setDeductionType(e.target.value)
              const t = (types ?? []).find(x => x.id === e.target.value)
              if (t && !name.trim()) setName(t.name)
            }}
          hint="Define la cuenta contable a la que se imputa el descuento en la centralización."
        >
          <option value="">— Sin tipo (cuenta genérica) —</option>
          {(types ?? []).map(t => (
          <option key={t.id} value={t.id}>{t.name}</option>
          ))}
        </SelectField>
        <div className="grid grid-cols-2 gap-3">
          <TextField
            dense
            label="Nombre *"
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="Seguro Complementario"
          />
          <div>
            <label htmlFor="deductionstab-monto-mensual" className={labelCls}>Monto mensual *</label>
            <NumericInput id="deductionstab-monto-mensual" value={amount} onChange={setAmount} locale={locale} decimals={2}
              min={0} className={inputCls} placeholder="0" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label htmlFor="deductionstab-vigente-desde-2" className={labelCls}>Vigente desde</label>
            <DatePicker id="deductionstab-vigente-desde-2" value={from} onChange={setFrom} className={inputCls} />
          </div>
          <div>
            <label htmlFor="deductionstab-vigente-hasta" className={labelCls}>Vigente hasta</label>
            <DatePicker id="deductionstab-vigente-hasta" value={to} onChange={setTo} className={inputCls} />
          </div>
        </div>
        {isEdit && (
          <label className="flex items-center gap-2 text-sm text-content">
            <input type="checkbox" checked={isActive} onChange={e => setIsActive(e.target.checked)} />
            Activo (se aplica cada mes mientras esté vigente)
          </label>
        )}
        <p className="text-xs text-content-subtle">
          Los descuentos recurrentes se aplican automáticamente cada mes mientras estén vigentes y activos.
        </p>
      </div>
    </Modal>
  )
}

function LoanModal({ loan, onClose }: { loan?: EmployeeLoan; onClose: () => void }) {
  const isEdit = !!loan
  const { data: emps } = useEmployees({ active: 'true', page_size: 9999 })
  const save = useSaveLoan()
  const toast = useToast()
  const locale = useTenantLocale()
  const [employee, setEmployee] = useState(loan?.employee || '')
  const [loanType, setLoanType] = useState(loan?.loan_type || 'company')
  const [name, setName] = useState(loan?.name || '')
  const [totalAmount, setTotalAmount] = useState(loan?.total_amount || '')
  const [installments, setInstallments] = useState(loan ? String(loan.total_installments) : '12')
  const [installmentAmount, setInstallmentAmount] = useState(loan?.installment_amount || '')
  const [paidInstallments, setPaidInstallments] = useState(loan ? String(loan.paid_installments) : '0')
  const [startDate, setStartDate] = useState(loan?.start_date || '')
  const [folio, setFolio] = useState(loan?.folio || '')
  const [status, setStatus] = useState(loan?.status || 'active')

  const handleSubmit = async () => {
    if (!employee || !name.trim() || !totalAmount || !installments || !startDate) {
      toast.error('Completa empleado, nombre, monto total, cuotas e inicio.')
      return
    }
    const installmentValue = installmentAmount ||
      String(Math.round(Number(totalAmount) / Number(installments)))
    try {
      await save.mutateAsync({
        id: loan?.id,
        data: {
          employee, loan_type: loanType, name: name.trim(),
          total_amount: totalAmount, total_installments: Number(installments),
          installment_amount: installmentValue, start_date: startDate,
          paid_installments: Number(paidInstallments),
          folio: folio.trim(), status,
        },
      })
      toast.success(isEdit ? 'Préstamo actualizado' : 'Préstamo creado')
      onClose()
    } catch {
      toast.error(isEdit ? 'Error al actualizar préstamo' : 'Error al crear préstamo')
    }
  }

  return (
    <Modal open title={isEdit ? 'Editar Préstamo / Crédito' : 'Nuevo Préstamo / Crédito'} onClose={onClose} size="sm"
      actions={
        <>
          <button onClick={onClose} className="px-4 py-2 text-sm rounded-lg border border-divider hover:bg-soft">Cancelar</button>
          <button onClick={handleSubmit} disabled={save.isPending} className="px-4 py-2 text-sm rounded-lg bg-primary text-on-primary hover:bg-primary-hover disabled:opacity-60">
            {save.isPending ? 'Guardando…' : 'Guardar'}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label htmlFor="deductionstab-empleado-prestamo" className={labelCls}>Empleado *</label>
            {isEdit ? (
              <input aria-label="Empleado" value={loan?.employee_name || ''} disabled className={inputCls} />
            ) : (
              <EntityAutocomplete id="deductionstab-empleado-prestamo"
                options={(emps?.employees || []).map((e: any) => ({ id: e.id, label: e.full_name }))}
                value={employee}
                onChange={setEmployee}
                placeholder="— Seleccionar —"
                required
              />
            )}
          </div>
          <SelectField
            dense
            label="Tipo"
            value={loanType}
            onChange={e => setLoanType(e.target.value)}
          >
            <option value="company">Préstamo Empresa</option>
            <option value="ccaf">Crédito CCAF</option>
            <option value="mutual">Crédito Mutual</option>
            <option value="sindicato">Cuota Sindicato</option>
            <option value="judicial">Retención Judicial</option>
            <option value="other">Otro</option>
          </SelectField>
        </div>
        <TextField
          dense
          label="Nombre *"
          value={name}
          onChange={e => setName(e.target.value)}
          placeholder="Crédito CCAF Los Andes"
        />
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label htmlFor="deductionstab-monto-total" className={labelCls}>Monto Total *</label>
            <NumericInput id="deductionstab-monto-total" value={totalAmount} onChange={setTotalAmount} locale={locale} decimals={2}
              min={0} className={inputCls} />
          </div>
          <div>
            <label htmlFor="deductionstab-cuotas" className={labelCls}>N° Cuotas *</label>
            {/* Entero: sin decimales (no existen "12,5 cuotas"). */}
            <NumericInput id="deductionstab-cuotas" value={installments} onChange={setInstallments} locale={locale} decimals={0}
              min={1} className={inputCls} />
          </div>
          <div>
            <label htmlFor="deductionstab-monto-cuota" className={labelCls}>Monto Cuota</label>
            <NumericInput id="deductionstab-monto-cuota" value={installmentAmount} onChange={setInstallmentAmount} locale={locale} decimals={2}
              min={0}
              placeholder={totalAmount && installments ? formatNumber(Number(totalAmount) / Number(installments), locale, 0) : 'Auto'}
              className={inputCls} />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label htmlFor="deductionstab-inicio-descuentos" className={labelCls}>Inicio descuentos *</label>
            <DatePicker id="deductionstab-inicio-descuentos" value={startDate} onChange={setStartDate} className={inputCls} />
          </div>
          <TextField
            dense
            label="Folio / Referencia"
            value={folio}
            onChange={e => setFolio(e.target.value)}
            placeholder="N° contrato CCAF"
          />
        </div>
        {isEdit && (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="deductionstab-cuotas-pagadas" className={labelCls}>Cuotas pagadas</label>
              <NumericInput id="deductionstab-cuotas-pagadas" value={paidInstallments} onChange={setPaidInstallments} locale={locale} decimals={0}
                min={0} className={inputCls} />
            </div>
            <SelectField
              dense
              label="Estado"
              value={status}
              onChange={e => setStatus(e.target.value)}
            >
              <option value="active">Activo</option>
              <option value="paid">Pagado</option>
              <option value="cancelled">Cancelado</option>
            </SelectField>
          </div>
        )}
      </div>
    </Modal>
  )
}

const hoyISO = () => todayISO()

/**
 * Estado real de un descuento. `is_active` no alcanza: al versionar un descuento
 * (cambio de monto desde una fecha) la versión anterior queda activa pero con
 * `effective_to` en el pasado, así que mostrarla como "Activo" confunde —
 * aparecen dos filas iguales por trabajador sin poder distinguirlas.
 */
type EstadoDeduccion = 'vigente' | 'programado' | 'terminado' | 'inactivo'

function estadoDe(d: RecurringDeduction, hoy = hoyISO()): EstadoDeduccion {
  if (!d.is_active) return 'inactivo'
  if (d.effective_to && d.effective_to < hoy) return 'terminado'
  if (d.effective_from && d.effective_from > hoy) return 'programado'
  return 'vigente'
}

const ESTADO_BADGE: Record<EstadoDeduccion, { label: string; cls: string }> = {
  vigente: { label: 'Vigente', cls: 'bg-success-surface-strong text-success-content' },
  programado: { label: 'Programado', cls: 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300' },
  terminado: { label: 'Terminado', cls: 'bg-surface-muted text-content-muted' },
  inactivo: { label: 'Inactivo', cls: 'bg-surface-muted text-content-muted' },
}

/**
 * "Cambiar valor desde una fecha": versiona los descuentos seleccionados. El monto
 * nuevo se edita POR TRABAJADOR (precargado con el actual) porque un mismo descuento
 * suele tener montos distintos según el plan de cada uno.
 */
function RepriceModal({ deductions, toast, onClose }: {
  deductions: RecurringDeduction[]
  toast: ReturnType<typeof useToast>
  onClose: () => void
}) {
  const [from, setFrom] = useState('')
  const [amounts, setAmounts] = useState<Record<string, string>>(
    // Valor tal cual viene, sin redondear: si el monto actual tiene decimales,
    // precargarlo entero cambiaría el valor sin que el usuario lo pida.
    () => Object.fromEntries(deductions.map(d => [d.id, String(d.amount)])),
  )
  const reprice = useRepriceDeductions()
  const locale = useTenantLocale()

  // Todos del mismo descuento es el caso habitual; si mezclaron, se avisa.
  const nombres = Array.from(new Set(deductions.map(d => d.name)))
  const prevDay = from
    ? new Date(new Date(from + 'T00:00:00').getTime() - 86400000).toLocaleDateString(locale)
    : null

  const submit = async () => {
    if (!from) { toast.error('Indica desde qué fecha rige el monto nuevo.'); return }
    const items = deductions.map(d => ({ id: d.id, amount: String(amounts[d.id] ?? d.amount) }))
    if (items.some(i => i.amount === '' || Number.isNaN(Number(i.amount)) || Number(i.amount) < 0)) {
      toast.error('Revisa los montos: deben ser números no negativos.')
      return
    }
    try {
      const res = await reprice.mutateAsync({ effective_from: from, items })
      toast.success(`${res.created} descuento(s) actualizados desde el ${new Date(from + 'T00:00:00').toLocaleDateString(locale)}.`)
      if (res.period_warning) toast.error(res.period_warning)
      if (res.errors?.length) res.errors.forEach(e => toast.error(e))
      onClose()
    } catch (e: any) {
      toast.error(e?.response?.data?.error?.message || 'No se pudo aplicar el cambio.')
    }
  }

  return (
    <Modal
      open onClose={onClose} title="Cambiar valor desde una fecha" size="lg"
      actions={(
        <>
          <button onClick={onClose} className="px-4 py-2 text-sm border border-divider rounded-lg hover:bg-soft text-content">
            Cancelar
          </button>
          <button onClick={submit} disabled={reprice.isPending}
            className="px-4 py-2 text-sm bg-primary text-on-primary rounded-lg hover:bg-primary-hover disabled:opacity-50">
            {reprice.isPending ? 'Aplicando…' : 'Aplicar'}
          </button>
        </>
      )}
    >
      <div className="space-y-4">
        <div>
          <label htmlFor="deductionstab-vigente-desde" className={labelCls}>Vigente desde *</label>
          <DatePicker id="deductionstab-vigente-desde" value={from} onChange={setFrom} className={inputCls} />
          <p className="mt-1 text-xs text-content-muted">
            El descuento actual quedará vigente hasta el día anterior
            {prevDay && <> (<strong>{prevDay}</strong>)</>} y se creará uno nuevo desde esta fecha.
          </p>
        </div>

        {/* Arriba y no al final: con muchas filas, un aviso bajo la tabla queda
            fuera de la vista justo cuando más tranquiliza leerlo. */}
        <p className="text-xs text-content-muted flex gap-2">
          <AlertTriangle size={14} className="shrink-0 mt-0.5" />
          <span>
            Las liquidaciones ya emitidas no se modifican: cada una guarda el monto con
            que se calculó. Si el período del mes elegido ya está calculado, recalcúlalo
            para que tome los montos nuevos.
          </span>
        </p>

        <div>
          <p className="text-sm text-content-muted mb-2">
            {nombres.length === 1
              ? <>Descuento: <strong className="text-content">{nombres[0]}</strong></>
              : <>Descuentos: <strong className="text-content">{nombres.join(', ')}</strong></>}
            {' '}({deductions.length} {deductions.length === 1 ? 'trabajador' : 'trabajadores'})
          </p>
          {/* Sin scroll propio: el Modal ya scrollea su contenido, y anidar dos
              scrolls cortaba la última fila a la mitad (parecía un glitch).

              ⚠️ Estaba anotada como «no se migra por el encabezado fijo», y ese
              encabezado NO funcionaba: su contenedor tenía `overflow-hidden`, que
              también se vuelve el contenedor de scroll del `sticky`, y como ese
              contenedor no scrollea, el encabezado se iba con las filas. Medido
              en el navegador el 2026-09-29. Migrarla no pierde nada; si hiciera
              falta fijarlo, es `maxHeight` del DataTable (con scroll propio). */}
          <DataTable
            caption="Montos nuevos por trabajador"
            minWidth={420}
            rows={deductions}
            rowKey={d => d.id}
            columns={[
              { key: 'trabajador', header: 'Trabajador', render: d => d.employee_name },
              { key: 'actual', header: 'Actual', numeric: true,
                render: d => <span className="text-content-muted">${formatNumber(Number(d.amount), locale, 0)}</span> },
              { key: 'nuevo', header: 'Nuevo', numeric: true,
                render: d => (
                  <NumericInput aria-label={`Nuevo monto — ${d.employee_name}`}
                    value={amounts[d.id] ?? ''}
                    onChange={v => setAmounts(a => ({ ...a, [d.id]: v }))}
                    locale={locale} decimals={2} min={0}
                    className={`${inputCls} text-right w-36 inline-block`}
                  />
                ) },
            ]}
          />
        </div>
      </div>
    </Modal>
  )
}

export default function DeductionsTab() {
  const [tab, setTab] = useState<'recurring' | 'loans'>('recurring')
  const [modal, setModal] = useState<'deduction' | 'loan' | 'bulk-deduction' | 'reprice' | null>(null)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [editingDeduction, setEditingDeduction] = useState<RecurringDeduction | null>(null)
  const [editingLoan, setEditingLoan] = useState<EmployeeLoan | null>(null)
  const { data: ded } = useDeductions()
  const { data: loans } = useLoans()
  const delDed = useDeleteDeduction()
  const delLoan = useDeleteLoan()
  const { can } = usePermission()
  const locale = useTenantLocale()
  const toast = useToast()
  // Una acción por operación: el backend mapea POST→create, PATCH→edit,
  // DELETE→delete sobre `payroll.payslips`. Antes todo colgaba de `create`, así
  // que la UI ofrecía botones que el backend iba a rechazar (el de eliminar ni
  // siquiera chequeaba permiso).
  const canCreate = can('payroll.payslips', 'create')
  const canEdit = can('payroll.payslips', 'edit')
  const canDelete = can('payroll.payslips', 'delete')

  const allDeductions = ded?.deductions || []
  const loanList = loans?.loans || []

  // Nombres distintos para el selector (un descuento aparece tantas veces como
  // trabajadores lo tengan asignado).
  const deductionNames = useMemo(
    () => Array.from(new Set(allDeductions.map(d => d.name))).sort((a, b) => a.localeCompare(b)),
    [allDeductions],
  )

  // Mismo sistema de filtros que el resto de los listados (buscador + selects
  // compactos). El valor '' de "Vigencia" es el default del hook, así que
  // "Vigentes" queda de fábrica sin contar como filtro activo.
  const filterConfig: FilterConfig = useMemo(() => [
    {
      key: 'search', type: 'search', placement: 'quick', grow: true,
      placeholder: 'Buscar por trabajador o descuento...',
    },
    {
      key: 'name', type: 'select', placement: 'quick', label: 'Descuento', widthClass: 'w-52',
      options: [{ value: '', label: 'Todos' }, ...deductionNames.map(n => ({ value: n, label: n }))],
    },
    {
      key: 'vigencia', type: 'select', placement: 'quick', label: 'Vigencia', widthClass: 'w-36',
      options: [
        { value: '', label: 'Vigentes' },
        { value: 'ended', label: 'No vigentes' },
        { value: 'all', label: 'Todos' },
      ],
    },
  ], [deductionNames])

  const filters = useFilters(filterConfig)

  const deductions = useMemo(() => {
    const q = (filters.params.search || '').trim().toLowerCase()
    const name = filters.values.name || ''
    const vigencia = filters.values.vigencia || ''
    const hoy = hoyISO()
    return allDeductions.filter(d => {
      if (name && d.name !== name) return false
      // Por defecto solo lo vigente: al versionar un descuento cada trabajador
      // pasa a tener 2 filas (la cerrada y la nueva) y la tabla se vuelve
      // ilegible. Las versiones anteriores siguen a un clic de distancia.
      const est = estadoDe(d, hoy)
      if (vigencia === '' && (est === 'terminado' || est === 'inactivo')) return false
      if (vigencia === 'ended' && est !== 'terminado' && est !== 'inactivo') return false
      if (!q) return true
      return (d.employee_name || '').toLowerCase().includes(q) || d.name.toLowerCase().includes(q)
    })
  }, [allDeductions, filters.params.search, filters.values.name, filters.values.vigencia])

  // La selección se acota a lo visible: si filtras, no arrastras filas ocultas a
  // una operación masiva sin querer.
  const visibleSelected = useMemo(
    () => deductions.filter(d => selected.has(d.id)),
    [deductions, selected],
  )
  const toggle = (id: string) => setSelected(s => {
    const next = new Set(s)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    return next
  })
  const toggleAllVisible = () => setSelected(s => {
    const next = new Set(s)
    const allOn = deductions.length > 0 && deductions.every(d => next.has(d.id))
    deductions.forEach(d => allOn ? next.delete(d.id) : next.add(d.id))
    return next
  })

  const closeModals = () => {
    setModal(null); setEditingDeduction(null); setEditingLoan(null); setSelected(new Set())
  }

  return (
    <>
      <ToastContainer toasts={toast.toasts} onDismiss={toast.dismiss} />

      <div className="mb-4 flex gap-1 border-b border-divider">
        <button onClick={() => setTab('recurring')}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${tab === 'recurring' ? 'border-primary text-primary' : 'border-transparent text-content-muted hover:text-content'}`}>
          Descuentos Recurrentes ({allDeductions.length})
        </button>
        <button onClick={() => setTab('loans')}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${tab === 'loans' ? 'border-primary text-primary' : 'border-transparent text-content-muted hover:text-content'}`}>
          Créditos / Préstamos ({loanList.length})
        </button>
      </div>

      {tab === 'recurring' && (
        <>
          <div className="flex justify-between items-center mb-3">
            <p className="text-sm text-content-muted">
              Descuentos fijos mensuales del trabajador (Seguro Complementario, FALP, cuotas, etc.).
            </p>
            {canCreate && (
              <div className="flex items-center gap-2">
                <button onClick={() => setModal('bulk-deduction')}
                  className="inline-flex items-center gap-1.5 px-3 py-2 text-sm border border-divider rounded-lg hover:bg-soft text-content">
                  <Users size={15} /> Asignar a varios
                </button>
                <button onClick={() => setModal('deduction')}
                  className="inline-flex items-center gap-1.5 px-3 py-2 text-sm bg-primary text-on-primary rounded-lg hover:bg-primary-hover">
                  <Plus size={15} /> Nuevo Descuento
                </button>
              </div>
            )}
          </div>
          {allDeductions.length > 0 && (
            <>
              <FilterBar config={filterConfig} filters={filters} />
              {deductions.length !== allDeductions.length && (
                <p className="-mt-2 mb-3 text-xs text-content-muted">
                  Mostrando {deductions.length} de {allDeductions.length}
                  {filters.values.vigencia === '' && ' — las versiones anteriores se ven eligiendo "No vigentes" en Vigencia.'}
                </p>
              )}
            </>
          )}

          {canCreate && visibleSelected.length > 0 && (
            <div className="mb-3 flex items-center gap-3 px-4 py-2.5 rounded-lg bg-soft border border-divider">
              <span className="text-sm text-content">
                {visibleSelected.length} seleccionado{visibleSelected.length === 1 ? '' : 's'}
              </span>
              <button onClick={() => setModal('reprice')}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-primary text-on-primary rounded-lg hover:bg-primary-hover">
                <CalendarClock size={15} /> Cambiar valor desde…
              </button>
              <button onClick={() => setSelected(new Set())}
                className="text-sm text-content-muted hover:text-content ml-auto">
                Quitar selección
              </button>
            </div>
          )}

          <DataTable<RecurringDeduction>
            columns={[
              // La casilla de selección es una columna más: el estado sigue
              // viviendo en la pantalla (`selected`), el componente solo la pinta.
              ...(canCreate ? [{
                key: 'sel',
                width: 40,
                header: (
                  <input
                    type="checkbox"
                    aria-label="Seleccionar todos los visibles"
                    checked={deductions.length > 0 && deductions.every(d => selected.has(d.id))}
                    onChange={toggleAllVisible}
                    className="cursor-pointer"
                  />
                ),
                render: (d: RecurringDeduction) => (
                  <input
                    type="checkbox"
                    aria-label={`Seleccionar ${d.employee_name} — ${d.name}`}
                    checked={selected.has(d.id)}
                    onChange={() => toggle(d.id)}
                    className="cursor-pointer"
                  />
                ),
              }] : []),
              { key: 'employee_name', header: 'Empleado', sortable: true,
                render: d => <span className="font-medium">{d.employee_name}</span> },
              { key: 'name', header: 'Descuento', sortable: true },
              { key: 'deduction_type_name', header: 'Tipo',
                render: d => (
                  <span className="text-xs text-content-muted">
                    {d.deduction_type_name || <span className="text-content-subtle italic">genérico</span>}
                  </span>
                ) },
              { key: 'amount', header: 'Monto Mensual', numeric: true,
                sortable: true, sortValue: d => Number(d.amount),
                render: d => `$${formatNumber(Number(d.amount), locale, 0)}` },
              { key: 'vigencia', header: 'Vigencia',
                // Ordena por la fecha de inicio cruda: el texto que se ve
                // ("sin inicio → 2026-03-01") no sirve para comparar.
                sortable: true, sortValue: d => d.effective_from ?? '',
                render: d => (
                  <span className="text-xs text-content-muted">
                    {d.effective_from || 'sin inicio'} → {d.effective_to || 'sin fin'}
                  </span>
                ) },
              { key: 'estado', header: 'Estado',
                sortable: true, sortValue: d => ESTADO_BADGE[estadoDe(d)].label,
                render: d => {
                  const badge = ESTADO_BADGE[estadoDe(d)]
                  return (
                    <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${badge.cls}`}>
                      {badge.label}
                    </span>
                  )
                } },
              { key: 'acciones', header: '', width: 80,
                render: d => (
                  <div className="flex justify-end gap-1 whitespace-nowrap">
                    {canEdit && (
                      <button onClick={() => setEditingDeduction(d)}
                        title="Editar"
                        className="p-1.5 rounded hover:bg-soft text-content-subtle hover:text-primary">
                        <Pencil size={14} />
                      </button>
                    )}
                    {canDelete && (
                      <button onClick={() => { if (confirm('¿Eliminar este descuento?')) delDed.mutate(d.id) }}
                        title="Eliminar"
                        className="p-1.5 rounded hover:bg-soft text-content-subtle hover:text-danger-solid">
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                ) },
            ]}
            rows={deductions}
            rowKey={d => d.id}
            rowClassName={d => (selected.has(d.id) ? 'bg-soft' : undefined)}
            caption="Descuentos recurrentes por trabajador"
            empty={
              <div className="text-center py-12 text-content-subtle text-sm">
                {allDeductions.length === 0
                  ? 'Sin descuentos recurrentes.'
                  : 'Ningún descuento coincide con el filtro.'}
              </div>
            }
          />
        </>
      )}

      {tab === 'loans' && (
        <>
          <div className="flex justify-between items-center mb-3">
            <p className="text-sm text-content-muted">
              Préstamos y créditos al trabajador con descuento por planilla (empresa, CCAF, mutual, sindicato, judicial).
            </p>
            {canCreate && (
              <button onClick={() => setModal('loan')}
                className="inline-flex items-center gap-1.5 px-3 py-2 text-sm bg-primary text-on-primary rounded-lg hover:bg-primary-hover">
                <Plus size={15} /> Nuevo Préstamo
              </button>
            )}
          </div>
          {loanList.length === 0 ? (
            <div className="text-center py-12 text-content-subtle text-sm">Sin prestamos registrados.</div>
          ) : (
            <DataTable
              columns={[
                { key: 'employee_name', header: 'Empleado', sortable: true,
                  render: ln => <span className="font-medium">{ln.employee_name}</span> },
                { key: 'name', header: 'Prestamo', sortable: true,
                  render: ln => (
                    <>
                      {ln.name}
                      {ln.folio && <span className="ml-1 text-xs text-content-subtle font-mono">#{ln.folio}</span>}
                    </>
                  ) },
                { key: 'loan_type', header: 'Tipo',
                  render: ln => <span className="text-content-muted">{ln.loan_type_display}</span> },
                { key: 'total_amount', header: 'Monto Total', numeric: true,
                  sortValue: ln => Number(ln.total_amount),
                  render: ln => `$${formatNumber(Number(ln.total_amount), locale, 0)}` },
                { key: 'installment_amount', header: 'Cuota', numeric: true,
                  sortValue: ln => Number(ln.installment_amount),
                  render: ln => `$${formatNumber(Number(ln.installment_amount), locale, 0)}` },
                { key: 'progreso', header: 'Progreso', width: 96,
                  render: ln => (
                    <div className="text-center text-xs">
                      {ln.paid_installments}/{ln.total_installments}
                    </div>
                  ) },
                { key: 'status', header: 'Estado', width: 112, sortable: true,
                  render: ln => (
                    <div className="text-center">
                      <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
                        ln.status === 'active' ? 'bg-info-surface-strong text-info-content'
                        : ln.status === 'paid' ? 'bg-success-surface-strong text-success-content'
                        : 'bg-surface-muted text-content-muted'}`}>
                        {ln.status_display}
                      </span>
                    </div>
                  ) },
                { key: 'acciones', header: <span className="sr-only">Acciones</span>, width: 88,
                  render: ln => (
                    <div className="text-right whitespace-nowrap">
                      {canEdit && (
                        <button onClick={() => setEditingLoan(ln)}
                          title="Editar"
                          className="p-1.5 rounded hover:bg-soft text-content-subtle hover:text-primary">
                          <Pencil size={14} />
                        </button>
                      )}
                      {canDelete && (
                        <button onClick={() => { if (confirm('Eliminar este prestamo?')) delLoan.mutate(ln.id) }}
                          className="p-1.5 rounded hover:bg-soft text-content-subtle hover:text-danger">
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  ) },
              ]}
              rows={loanList}
              rowKey={ln => ln.id}
              minWidth={980}
            />
          )}
        </>
      )}

      {(modal === 'deduction' || editingDeduction) && (
        <DeductionModal deduction={editingDeduction ?? undefined} onClose={closeModals} />
      )}
      {modal === 'bulk-deduction' && <BulkAssignModal mode="deduction" toast={toast} onClose={closeModals} />}
      {modal === 'reprice' && visibleSelected.length > 0 && (
        <RepriceModal deductions={visibleSelected} toast={toast} onClose={closeModals} />
      )}
      {(modal === 'loan' || editingLoan) && (
        <LoanModal loan={editingLoan ?? undefined} onClose={closeModals} />
      )}
    </>
  )
}
